<template>
    <div v-loading.fullscreen.lock="fullscreenLoading" class="campaign-create">
        <div :class="['page-header', { sticky: isSticky }]">
            <h1 class="page-title">{{ isEditMode ? $t('messages.edit_campaign') : $t('messages.create_new_campaign') }}
            </h1>
            <button class="btn-primary" @click="saveCampaign" :disabled="!canSave">
                {{ isEditMode ? $t('messages.update') : $t('messages.save') }}
            </button>
        </div>

        <div class="campaign-content">
            <div class="campaign-sidebar">
                <div class="sidebar-nav">
                    <div v-for="(step, index) in steps" :key="step.id" :class="['nav-item', {
                        active: currentStep === step.id
                    }]" @click="goToStep(step.id)">
                        <div class="nav-icon">
                            <img :src="step.icon" :alt="step.title" />
                        </div>
                        <div class="nav-text">{{ step.title }}</div>
                    </div>
                </div>
            </div>

            <div class="campaign-main">
                <div id="main-settings" class="settings-block">
                    <MainSettings :campaign="campaignData" :agencies="localAgencies"
                        :advertiser_categories="localAdvertiserCategories" :advertisers="localAdvertisers"
                        :products="localProducts" @update="updateCampaign" @created="handleEntityCreated" />
                </div>

                <div id="budget-schedule" class="settings-block">
                    <BudgetSchedule :campaign="campaignData" :server-campaign="campaign" :currencies="currencies"
                        :lock-currency="true" :is-edit-mode="isEditMode" :is-test="!!campaignData.is_test"
                        @update="updateCampaign" />
                </div>

                <div id="reach-brand-safety" class="settings-block">
                    <ReachBrandSafety ref="reachBrandSafety" :campaign="{
                        frequency: campaignData.frequency,
                        period_type: campaignData.period_type,
                        period_value: campaignData.period_value,
                        frequency_is_active: campaignData.frequency_is_active,
                        brandSafetySegments: campaignData.brandSafetySegments
                    }" :brand-safety-segments="localBrandSafetySegments" @update="updateCampaign" />
                </div>

                <div id="commissions" v-if="isAltel" class="settings-block">
                    <Commissions :campaign="campaignData" @update="updateCampaign" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import MainSettings from './steps/MainSettings.vue';
import BudgetSchedule from './steps/BudgetSchedule.vue';
import ReachBrandSafety from './steps/ReachBrandSafety.vue';
import Commissions from './steps/Commissions.vue';

export default {
    name: "CampaignCreate",
    components: {
        MainSettings,
        BudgetSchedule,
        ReachBrandSafety,
        Commissions,
    },
    props: {
        campaign: { type: Object, default: () => ({}) },
        currencies: { type: Array, default: () => [] },
        campaignId: { type: [String, Number], default: null },
        mode: { type: String, default: '' },
        user: { type: Object, default: () => ({}) },

        agencies: { type: Array, default: () => [] },
        advertiser_categories: { type: Array, default: () => [] },
        advertisers: { type: Array, default: () => [] },
        products: { type: Array, default: () => [] },
        brand_safety_segments: { type: Array, default: () => [] }
    },
    data() {
        return {
            currentStep: 'main-settings',
            fullscreenLoading: false,
            isSticky: false,
            campaignData: this.initializeCampaignData(),
            localBrandSafetySegments: []
        };
    },
    created() {
        this.localAgencies = [...(this.agencies || [])];
        this.localAdvertiserCategories = [...(this.advertiser_categories || [])];
        this.localAdvertisers = [...(this.advertisers || [])];
        this.localProducts = [...(this.products || [])];
        this.localBrandSafetySegments = [...(this.brand_safety_segments || [])];
    },
    computed: {
        isEditMode() {
            return this.mode === 'edit' && !!this.campaignId;
        },
        isAltel() {
            return this.user && (this.user.role_id === 2 || this.user.role === 'altel');
        },
        steps() {
            const s = [
                {
                    id: 'main-settings',
                    title: this.$t('messages.main_settings'),
                    icon: '/images/settings.svg'
                },
                {
                    id: 'budget-schedule',
                    title: this.$t('messages.budget_schedule'),
                    icon: '/images/calendar-coin.svg'
                },
                {
                    id: 'reach-brand-safety',
                    title: this.$t('messages.reach_brand_safety'),
                    icon: '/images/globe-lock.svg'
                }
            ];

            if (this.isAltel) {
                s.push({
                    id: 'commissions',
                    title: this.$t('messages.commissions'),
                    icon: '/images/percent.svg'
                });
            }

            return s;
        },
        canSave() {
            return !!this.campaignData.name &&
                !!this.campaignData.product_id &&
                !!this.campaignData.agency_id &&
                !!this.campaignData.advertiser_id &&
                this.validateScheduleData();
        }
    },
    watch: {
        // Ensure campaignData is populated when the server-provided `campaign` prop arrives or changes
        campaign: {
            handler(newCampaign) {
                if (this.isEditMode && newCampaign) {
                    this.campaignData = this.mapCampaignToFormData(newCampaign);
                }
            },
            immediate: true
        }
    },
    mounted() {
        this.setupScrollListener();

        if (this.isEditMode && this.campaign) {
            this.campaignData = this.mapCampaignToFormData(this.campaign);
        } else {
            // prefill commissions to 20% for Altel users
            if (this.isAltel) {
                this.campaignData.commissions = this.campaignData.commissions || 20;
            }
        }
    },
    beforeUnmount() {
        this.removeScrollListener();
    },
    methods: {
        initializeCampaignData() {
            if (this.campaign && this.isEditMode) {
                return this.mapCampaignToFormData(this.campaign);
            }

            return {
                name: '',
                status: 'active',
                is_test: false,
                // Default currency: prefer first available currency, fallback to USD
                // Always default to KZT as required
                currency: 'KZT',
                totalBudget: 0.00,
                schedule: [{
                    startDate: this.getTodayDate(),
                    startTime: '00:00',
                    endDate: this.getTodayDate(),
                    endTime: '23:59',
                    budget: 0.00
                }],
                lifeTimeDistribution: 'evenly',
                brandSafetySegments: [],
                frequency: null,
                period_type: 'day',
                period_value: 1,
                frequency_is_active: true,

                // entity selections
                product_id: null,
                agency_id: null,
                advertiser_id: null,
                advertiser_category_id: null,

                commissions: 0,
            };
        },

        mapCampaignToFormData(campaign) {
            return {
                product_id: campaign.product_id ?? (campaign.product && campaign.product.id) ?? null,
                agency_id: campaign.agency_id ?? (campaign.agency && campaign.agency.id) ?? null,
                advertiser_id: campaign.advertiser_id ?? (campaign.advertiser && campaign.advertiser.id) ?? null,
                advertiser_category_id: campaign.advertiser_category_id ?? (campaign.advertiser_category && campaign.advertiser_category.id) ?? null,

                name: campaign.campaign || '',
                status: campaign.status || 'active',
                is_test: !!(campaign.is_test ?? campaign.isTest),
                currency: 'KZT',
                totalBudget: campaign.total_budget != null ? campaign.total_budget : (campaign.totalBudget != null ? campaign.totalBudget : 0),
                schedule: this.mapScheduleData(campaign.schedules),
                lifeTimeDistribution: campaign.lifetime_distribution || 'evenly',

                brandSafetySegments: this.mapBrandSafetySegments(campaign.brand_safety_segments),
                frequency: campaign.frequency ?? null,
                period_type: campaign.period_type ?? 'day',
                period_value: campaign.period_value ?? 1,
                frequency_is_active: campaign.frequency_is_active ?? true,

                commissions: campaign.commissions || 0,
            };
        },

        mapScheduleData(schedules) {
            if (!schedules || schedules.length === 0) {
                return [{
                    startDate: this.getTodayDate(),
                    startTime: '00:00',
                    endDate: this.getTodayDate(),
                    endTime: '23:59',
                    budget: 0.00
                }];
            }

            return schedules.map(schedule => ({
                startDate: schedule.start_date ? schedule.start_date.split('T')[0] : this.getTodayDate(),
                startTime: schedule.start_time || '00:00',
                endDate: schedule.end_date ? schedule.end_date.split('T')[0] : this.getTodayDate(),
                endTime: schedule.end_time || '23:59',
                budget: schedule.budget != null ? parseFloat(schedule.budget) : 0
            }));
        },

        mapBrandSafetySegments(brandSafetySegments) {
            if (!brandSafetySegments) return [];
            return brandSafetySegments.split(';').filter(id => id).map(id => parseInt(id));
        },

        setupScrollListener() {
            this.scrollHandler = this.handleScroll.bind(this);
            window.addEventListener('scroll', this.scrollHandler);
        },

        removeScrollListener() {
            if (this.scrollHandler) {
                window.removeEventListener('scroll', this.scrollHandler);
            }
        },

        handleScroll() {
            const scrollPosition = window.scrollY;
            const windowHeight = window.innerHeight;
            const headerHeight = 70;

            this.isSticky = scrollPosition > headerHeight;

            const sections = this.steps.map(step => ({
                id: step.id,
                element: document.getElementById(step.id)
            }));

            let newStep = 'main-settings';

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                if (section.element) {
                    const rect = section.element.getBoundingClientRect();
                    const elementTop = rect.top + scrollPosition;

                    if (scrollPosition >= elementTop - windowHeight / 2) {
                        newStep = section.id;
                        break;
                    }
                }
            }

            if (newStep !== this.currentStep) {
                this.currentStep = newStep;
            }
        },

        goToStep(stepId) {
            this.currentStep = stepId;

            const element = document.getElementById(stepId);
            if (element) {
                const headerHeight = 70;
                const offsetTop = element.offsetTop - headerHeight - 20;

                window.scrollTo({
                    top: offsetTop,
                    behavior: 'smooth'
                });
            }
        },

        updateCampaign(data) {
            const wasTest = !!this.campaignData.is_test;
            this.campaignData = { ...this.campaignData, ...data };
            if (this.campaignData.is_test && !wasTest) {
                this.zeroBudgets();
            }
        },
        zeroBudgets() {
            this.campaignData.totalBudget = 0;
            if (Array.isArray(this.campaignData.schedule)) {
                this.campaignData.schedule = this.campaignData.schedule.map((period) => ({
                    ...period,
                    budget: 0,
                }));
            }
        },
        handleEntityCreated(payload) {
            // payload: { type, item }
            if (!payload || !payload.type || !payload.item) return;
            const { type, item } = payload;
            if (type === 'agency') {
                this.localAgencies.unshift(item);
            } else if (type === 'category') {
                this.localAdvertiserCategories.unshift(item);
            } else if (type === 'advertiser') {
                this.localAdvertisers.unshift(item);
            } else if (type === 'product') {
                this.localProducts.unshift(item);
                // auto-select created product
                this.campaignData.product_id = item.id;
            }
        },

        goBack() {
            window.location.href = '/campaigns';
        },

        getTodayDate() {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        validateScheduleData() {
            if (!this.campaignData.schedule || !Array.isArray(this.campaignData.schedule)) {
                return false;
            }

            const allPeriodsValid = this.campaignData.schedule.every(period => {
                const budget = typeof period.budget === 'number' ? period.budget : parseFloat(period.budget);

                return period.startDate &&
                    period.endDate &&
                    period.startTime &&
                    period.endTime &&
                    period.budget !== undefined &&
                    period.budget !== null &&
                    period.budget !== '' &&
                    !isNaN(budget) &&
                    budget >= 0;
            });

            const budgetTotalOk = this.campaignData.is_test || this.campaignData.schedule.some((period) => {
                const budget = typeof period.budget === 'number' ? period.budget : parseFloat(period.budget);
                return !isNaN(budget) && budget > 0;
            });

            return allPeriodsValid && budgetTotalOk;
        },

        validateAllData() {
            const errors = [];

            if (!this.validateScheduleData()) {
                errors.push(this.$t('messages.error_fill_schedule_fields'));
            }

            return {
                isValid: errors.length === 0,
                errors: errors
            };
        },

        async saveCampaign() {
            this.fullscreenLoading = true;

            try {
                // Validate all data before sending
                const validation = this.validateAllData();
                if (!validation.isValid) {
                    this.$notify({
                        title: this.$t('messages.validation_error_title'),
                        message: validation.errors.join('\n'),
                        type: "error",
                    });
                    this.fullscreenLoading = false;
                    return;
                }
                // Ensure we have the latest frequency values from ReachBrandSafety (synchronous)
                try {
                    const reachFreq = this.$refs.reachBrandSafety && this.$refs.reachBrandSafety.getCurrentFrequency && this.$refs.reachBrandSafety.getCurrentFrequency();
                    if (reachFreq) {
                        this.campaignData.frequency = reachFreq.frequency;
                        this.campaignData.period_type = reachFreq.period_type;
                        this.campaignData.period_value = reachFreq.period_value;
                        this.campaignData.frequency_is_active = reachFreq.frequency_is_active;
                    }
                } catch (e) { }

                const campaignData = {
                    name: this.campaignData.name,
                    status: this.campaignData.status,
                    is_test: !!this.campaignData.is_test,
                    currency: this.campaignData.currency,
                    totalBudget: this.campaignData.totalBudget,
                    schedule: this.campaignData.schedule,
                    lifeTimeDistribution: this.campaignData.lifeTimeDistribution,
                    brandSafetySegments: this.campaignData.brandSafetySegments,
                    frequency: this.campaignData.frequency,
                    period_type: this.campaignData.period_type,
                    period_value: this.campaignData.period_value,
                    frequency_is_active: this.campaignData.frequency_is_active,
                    commissions: this.campaignData.commissions || 0
                };
                // include entity selections
                campaignData.product_id = this.campaignData.product_id || null;
                campaignData.agency_id = this.campaignData.agency_id || null;
                campaignData.advertiser_id = this.campaignData.advertiser_id || null;
                campaignData.advertiser_category_id = this.campaignData.advertiser_category_id || null;

                let url, method;
                if (this.isEditMode) {
                    url = `/campaigns/${this.campaignId}`;
                    method = 'PUT';
                } else {
                    url = '/campaigns/create';
                    method = 'POST';
                }

                const response = await fetch(url, {
                    method: method,
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                    },
                    body: JSON.stringify(campaignData)
                });

                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    const text = await response.text();
                    throw new Error(this.$t('messages.error_unexpected_server_response', { status: response.status }));
                }

                const result = await response.json();

                if (response.ok) {
                    this.$notify({
                        title: this.$t('messages.completed_successfully'),
                        message: this.isEditMode ? this.$t('messages.campaign_updated_successfully') : this.$t('messages.campaign_created_successfully'),
                        type: "success",
                    });

                    window.location.href = "/campaigns";
                } else {
                    const fallback = this.isEditMode
                        ? this.$t('messages.error_updating_campaign')
                        : this.$t('messages.error_creating_campaign');
                    const message = result.errors
                        ? Object.values(result.errors).flat().join('\n')
                        : (result.message || fallback);
                    throw new Error(message);
                }

            } catch (error) {
                this.$notify({
                    title: this.$t('messages.error_occurred'),
                    message: error.message || (this.isEditMode ? this.$t('messages.error_occurred_updating_campaign') : this.$t('messages.error_occurred_creating_campaign')),
                    type: "error",
                });
            } finally {
                this.fullscreenLoading = false;
            }
        }
    }
};
</script>